import DashboardIcon from "@mui/icons-material/SpaceDashboardOutlined"
import ChatIcon from "@mui/icons-material/ForumOutlined"
import TaskIcon from "@mui/icons-material/TaskAltOutlined"
import ProjectIcon from "@mui/icons-material/FolderSpecialOutlined"
import ClientsIcon from "@mui/icons-material/BusinessOutlined"
import PeopleIcon from "@mui/icons-material/GroupsOutlined"
import ShieldIcon from "@mui/icons-material/AdminPanelSettingsOutlined"
import SettingsIcon from "@mui/icons-material/SettingsOutlined"
import TimeIcon from "@mui/icons-material/ScheduleOutlined"
import ReviewIcon from "@mui/icons-material/FactCheckOutlined"
import BillingIcon from "@mui/icons-material/PaymentsOutlined"
import InvoiceIcon from "@mui/icons-material/ReceiptLongOutlined"
import ReportIcon from "@mui/icons-material/InsertChartOutlined"
import DriveIcon from "@mui/icons-material/FolderOpenOutlined"
import VaultIcon from "@mui/icons-material/LockOutlined"

/**
 * The sidebar.
 *
 * Each item names the `module:action` it needs. Nothing here decides access — the
 * server does — but an item the user cannot use is hidden rather than shown and
 * then refused on click, which reads as a broken app rather than a restricted one.
 *
 * Items with no `permission` are always shown: everybody has a dashboard and
 * everybody has settings.
 */
export const NAV_ITEMS = [
  { id: "dashboard", label: "Dashboard", to: "/", icon: DashboardIcon, exact: true },
  { id: "chat", label: "Chat", to: "/chat", icon: ChatIcon, permission: ["chat", "view"], badge: "chat" },
  { id: "tasks", label: "Tasks & Issues", to: "/tasks", icon: TaskIcon, permission: ["tasks", "view"] },
  { id: "projects", label: "Projects", to: "/projects", icon: ProjectIcon, permission: ["projects", "view"] },
  /* The store and the vault sit with the work rather than under Administration:
     they are things people open several times a day, not settings. */
  { id: "drive", label: "Files", to: "/files", icon: DriveIcon, permission: ["drive", "view"] },
  { id: "vault", label: "Password Vault", to: "/vault", icon: VaultIcon, permission: ["vault", "view"] },
  { id: "timesheet", label: "My timesheet", to: "/timesheet", icon: TimeIcon, permission: ["time_tracking", "log"] },
  /* Reviewing is a manager's job, so the item follows the permission rather than
     the role — a role built by hand that happens to hold sign_off gets it too. */
  { id: "time-review", label: "Time review", to: "/time-review", icon: ReviewIcon, permission: ["time_tracking", "view_all"] },
  /* Its own module, so its own permission — an admin can hand somebody the
     totals without handing them the timer or anybody's individual entries. */
  { id: "time-reports", label: "Time reports", to: "/time-reports", icon: ReportIcon, permission: ["time_reports", "view"] },
  /* A client reads invoices; the back office bills. Two items, because "Billing"
     on a client's sidebar would promise something they cannot do. */
  { id: "invoices", label: "Invoices", to: "/invoices", icon: InvoiceIcon, permission: ["billing", "view"] },
  { id: "billing", label: "Billing", to: "/billing", icon: BillingIcon, permission: ["billing", "invoice"], section: "Administration" },
  { id: "clients", label: "Clients", to: "/clients", icon: ClientsIcon, permission: ["clients", "view"], section: "Administration" },
  { id: "users", label: "Users", to: "/users", icon: PeopleIcon, permission: ["users", "view"], section: "Administration" },
  { id: "roles", label: "Roles & Permissions", to: "/roles", icon: ShieldIcon, permission: ["rbac_admin", "view"], section: "Administration" },
  { id: "settings", label: "Settings", to: "/settings", icon: SettingsIcon, section: "Administration" },
]

/** The items this user can actually reach, grouped by section. */
export function visibleNavSections(can) {
  const visible = NAV_ITEMS.filter((item) => !item.permission || can(item.permission[0], item.permission[1]))

  const sections = new Map()
  for (const item of visible) {
    const key = item.section || ""
    if (!sections.has(key)) sections.set(key, [])
    sections.get(key).push(item)
  }
  return [...sections.entries()].map(([title, items]) => ({ title, items }))
}
